// ────────────────────────────────────────────────────────────────────────────
//  Org.tsx — Komplettes Organisations-Menü
//
//  Tabs (für Member sichtbar je nach Rolle):
//   - Übersicht  (alle)
//   - Mitglieder (alle, aber Aktionen nur Owner/Admin)
//   - Einladungen (Owner + Admin mit canManageInviteCodes)
//   - Einstellungen (Owner)
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useT, useLangCode } from '../i18n'
import { useAuth } from '../lib/useAuth'
import {
  useOrg, useOrgMembers,
  previewInviteCode, joinOrgWithCode, createOrg, leaveOrg,
  setMemberRole, setMemberPermissions, removeMember,
  listInviteCodes, createInviteCode, deleteInviteCode,
  sendEmailInvite, updateOrg, deleteOrg,
  type OrgMember, type OrgPreview, type InviteCode, type OrgDetail,
} from '../lib/useOrg'
import { useExtensions, canCreateOrg } from '../lib/useExtensions'
import { API_ORIGIN } from '../lib/api'

type Tab = 'overview' | 'members' | 'permissions' | 'invites' | 'settings'

export function Org() {
  const { org, loading, refresh } = useOrg()
  if (loading) return <Spinner />
  if (!org) return <JoinView onJoined={refresh} />
  return <OrgPanel org={org} onChanged={refresh} />
}

// ── Haupt-Panel mit Tabs ────────────────────────────────────────────────────
function OrgPanel({ org, onChanged }: { org: OrgDetail; onChanged: () => void }) {
  const t = useT()
  const { user } = useAuth()
  const { items: members, refresh: refreshMembers } = useOrgMembers(true)
  const [tab, setTab] = useState<Tab>('overview')

  const me = members.find(m => m.userId === user?.id)
  const myRole = (me?.orgRole ?? user?.orgRole ?? 'Member') as 'Owner' | 'Admin' | 'Member'
  const isOwner = myRole === 'Owner'
  const canManageInvites = isOwner || (me?.permissions?.canManageInviteCodes ?? false)
  const canManageMembers = isOwner || (me?.permissions?.canManageMembers ?? false)

  const tabs: { id: Tab; key: string; show: boolean }[] = [
    { id: 'overview',    key: 'org.tabOverview',    show: true },
    { id: 'members',     key: 'org.tabMembers',     show: true },
    { id: 'permissions', key: 'org.tabPermissions', show: isOwner },
    { id: 'invites',     key: 'org.tabInvites',     show: canManageInvites },
    { id: 'settings',    key: 'org.tabSettings',    show: isOwner },
  ]

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-5 sm:py-7 md:py-10 space-y-4 sm:space-y-5">
      <header className="mb-2">
        <h1 className="text-3xl md:text-4xl font-bold">{t('org.title')}</h1>
        <p className="text-sm text-app-text-dim mt-1.5">{t('org.sub')}</p>
      </header>

      {/* Tabs */}
      <nav className="overflow-x-auto -mx-4 md:mx-0 pl-4 md:pl-0">
        <div className="inline-flex gap-1 p-1 rounded-xl"
             style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
          {tabs.filter(x => x.show).map(x => (
            <button
              key={x.id}
              onClick={() => setTab(x.id)}
              className={`px-4 py-2 rounded-lg transition text-sm font-medium whitespace-nowrap ${
                tab === x.id ? 'bg-accent text-white shadow-md shadow-accent/30'
                            : 'text-app-text-dim hover:text-app-text'
              }`}
            >
              {t(x.key)}
            </button>
          ))}
        </div>
      </nav>

      {tab === 'overview'    && <OverviewTab org={org} myRole={myRole} onLeave={onChanged} />}
      {tab === 'members'     && <MembersTab members={members} isOwner={isOwner} canManage={canManageMembers} myUserId={user?.id ?? ''} refresh={refreshMembers} />}
      {tab === 'permissions' && <PermissionsTab members={members} myUserId={user?.id ?? ''} refresh={refreshMembers} />}
      {tab === 'invites'     && <InvitesTab />}
      {tab === 'settings'    && <SettingsTab org={org} onChanged={onChanged} />}
    </div>
  )
}

// ── Tab: Berechtigungen-Übersicht ───────────────────────────────────────────
function PermissionsTab({ members, myUserId, refresh }: { members: OrgMember[]; myUserId: string; refresh: () => void }) {
  const t = useT()
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Owner-Member am Anfang, dann Admins, dann Member; eigener Eintrag ans Ende falls nicht Owner
  const sorted = [...members].sort((a, b) => {
    const order = (r: string) => r === 'Owner' ? 0 : r === 'Admin' ? 1 : 2
    return order(a.orgRole) - order(b.orgRole)
  })

  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4">
        <h3 className="font-semibold flex items-center gap-2"><span className="text-lg">🛂</span>{t('org.permsOverview')}</h3>
        <p className="text-xs text-app-text-mute mt-0.5">{t('org.permsOverviewSub')}</p>
      </header>
      <ul className="space-y-2">
        {sorted.map(m => (
          <PermissionsRow
            key={m.id}
            m={m}
            isMe={m.userId === myUserId}
            expanded={expanded.has(m.id)}
            onToggle={() => toggle(m.id)}
            onChanged={refresh}
          />
        ))}
      </ul>
    </section>
  )
}

function PermissionsRow({ m, isMe, expanded, onToggle, onChanged }: {
  m: OrgMember; isMe: boolean; expanded: boolean; onToggle: () => void; onChanged: () => void
}) {
  const t = useT()
  const isOwnerRow = m.orgRole === 'Owner'
  const editable = !isMe && !isOwnerRow

  // Counter aktiver Permissions (nur info)
  const activeCount = Object.values(m.permissions).filter(Boolean).length
  const totalCount = Object.keys(m.permissions).length
  const initials = m.displayName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()

  return (
    <li className="rounded-xl glass-flat overflow-hidden">
      <button onClick={onToggle} className="w-full text-left flex items-center gap-3 p-3 hover:bg-app-elev transition">
        {m.avatarUrl ? (
          <img src={m.avatarUrl.startsWith('http') ? m.avatarUrl : `${API_ORIGIN}${m.avatarUrl}`}
               alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full grid place-items-center text-xs font-bold text-white bg-gradient-to-br from-accent to-accent2">
            {initials || '?'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate flex items-center gap-1.5">
            {m.displayName}{isMe && <span className="text-[10px] text-app-text-mute">{t('org.yourselfBadge')}</span>}
          </div>
          <div className="text-xs text-app-text-mute">
            {isOwnerRow ? t('org.roleOwner') : `${activeCount} / ${totalCount} ${t('org.permsTitle').toLowerCase()}`}
          </div>
        </div>
        <RolePill role={m.orgRole as any} small />
        {editable && (
          <span className="text-app-text-mute text-sm transition" style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
        )}
      </button>

      {expanded && editable && <PermissionsEditor m={m} onChanged={onChanged} />}
    </li>
  )
}

function PermissionsEditor({ m, onChanged }: { m: OrgMember; onChanged: () => void }) {
  const t = useT()
  const [perms, setPerms] = useState<OrgMember['permissions']>(m.permissions)
  const [saving, setSaving] = useState(false)
  const [ok, setOk] = useState(false)

  const toggle = async (k: keyof OrgMember['permissions']) => {
    const next = { ...perms, [k]: !perms[k] }
    setPerms(next)
    setSaving(true); setOk(false)
    try {
      await setMemberPermissions(m.id, next)
      setOk(true)
      setTimeout(() => setOk(false), 1200)
      onChanged()
    } catch {
      // Rollback bei Fehler
      setPerms(perms)
    } finally { setSaving(false) }
  }

  return (
    <div className="px-3 pb-3 pt-1" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="grid sm:grid-cols-2 gap-1 mt-2">
        {(Object.keys(perms) as Array<keyof OrgMember['permissions']>).map(k => (
          <label key={k} className="flex items-center gap-3 p-2 rounded-lg hover:bg-app-elev cursor-pointer">
            <input type="checkbox" checked={perms[k]} onChange={() => toggle(k)} disabled={saving}
              className="w-4 h-4 rounded accent-accent shrink-0" />
            <span className="text-sm">{t(`org.permLabels.${k}`)}</span>
          </label>
        ))}
      </div>
      {ok && <div className="text-[11px] text-teal mt-2 text-right">{t('org.permsSaved')}</div>}
    </div>
  )
}

// ── Tab: Übersicht ──────────────────────────────────────────────────────────
function OverviewTab({ org, myRole, onLeave }: { org: OrgDetail; myRole: string; onLeave: () => void }) {
  const t = useT()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const isOwner = myRole === 'Owner'
  const handleLeave = async () => {
    if (isOwner) { setErr('Owner können die Org nicht verlassen — nutze „Auflösen" in den Einstellungen.'); return }
    if (!confirm(t('org.leaveConfirm', { name: org.name }))) return
    setBusy(true); setErr(null)
    try { await leaveOrg(); onLeave() }
    catch (e: any) { setErr(e?.message ?? 'leave failed') }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-5">
      <section className="glass-strong p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-12 w-72 h-72 rounded-full opacity-50"
             style={{ background: 'radial-gradient(circle, rgba(58,98,255,0.20), transparent 70%)' }} />
        <div className="relative flex flex-col sm:flex-row gap-5 items-start">
          <OrgLogo logoUrl={org.logoUrl} name={org.name} />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold tracking-wider uppercase text-app-text-mute">{t('org.youBelong')}</div>
            <h2 className="text-3xl font-bold mt-0.5 truncate">{org.name}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <RolePill role={myRole as any} />
              <Pill icon="👥" text={org.memberLimit
                  ? t('org.membersOf', { n: org.memberCount, limit: org.memberLimit })
                  : `${org.memberCount} ${t('org.members')}`} />
              {org.activeExtensions?.length > 0 && (
                <Pill icon="💎" text={`${org.activeExtensions.length} ${t('org.activeExt')}`} />
              )}
            </div>
            {!isOwner && (
              <button onClick={handleLeave} disabled={busy}
                className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-app-text-mute hover:text-coral transition disabled:opacity-50">
                {busy ? '…' : t('org.leaveBtn')}
              </button>
            )}
          </div>
        </div>
        {err && <div className="mt-4 text-sm text-coral">{err}</div>}
      </section>

      {org.activeExtensions?.length > 0 && (
        <section className="glass p-5 md:p-7">
          <header className="mb-4">
            <h3 className="font-semibold flex items-center gap-2"><span className="text-lg">💎</span>{t('org.activeExt')}</h3>
          </header>
          <div className="flex flex-wrap gap-2">
            {org.activeExtensions.map(slug => (
              <span key={slug}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                style={{ background: 'rgba(138,68,238,0.15)', border: '1px solid rgba(138,68,238,0.30)', color: '#8a44ee' }}>
                {productEmoji(slug)} {slug}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

// ── Tab: Mitglieder ────────────────────────────────────────────────────────
function MembersTab({
  members, isOwner, canManage, myUserId, refresh,
}: { members: OrgMember[]; isOwner: boolean; canManage: boolean; myUserId: string; refresh: () => void }) {
  const t = useT()
  const [permsModal, setPermsModal] = useState<OrgMember | null>(null)

  const handleToggleAdmin = async (m: OrgMember) => {
    if (!isOwner) return
    const next = m.orgRole === 'Admin' ? 'Member' : 'Admin'
    try { await setMemberRole(m.id, next as any); refresh() }
    catch (e: any) { alert(e?.message ?? 'failed') }
  }

  const handleRemove = async (m: OrgMember) => {
    if (!canManage) return
    if (!confirm(t('org.removeConfirm', { name: m.displayName }))) return
    try { await removeMember(m.userId); refresh() }
    catch (e: any) { alert(e?.message ?? 'failed') }
  }

  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <span className="text-lg">👥</span>{t('org.members')}
          <span className="text-app-text-mute font-normal text-sm">({members.length})</span>
        </h3>
      </header>
      <ul className="space-y-2">
        {members.map(m => (
          <MemberAdminRow
            key={m.id}
            m={m}
            isMe={m.userId === myUserId}
            isOwner={isOwner}
            canManage={canManage}
            onToggleAdmin={() => handleToggleAdmin(m)}
            onRemove={() => handleRemove(m)}
            onPerms={() => setPermsModal(m)}
            t={t}
          />
        ))}
      </ul>
      {permsModal && (
        <PermissionsModal
          member={permsModal}
          onClose={() => setPermsModal(null)}
          onSaved={() => { setPermsModal(null); refresh() }}
        />
      )}
    </section>
  )
}

function MemberAdminRow({
  m, isMe, isOwner, canManage, onToggleAdmin, onRemove, onPerms, t,
}: {
  m: OrgMember; isMe: boolean; isOwner: boolean; canManage: boolean
  onToggleAdmin: () => void; onRemove: () => void; onPerms: () => void
  t: any
}) {
  const initials = m.displayName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()
  const canEdit = !isMe && m.orgRole !== 'Owner' && (isOwner || canManage)
  return (
    <li className="flex flex-wrap items-center gap-3 p-3 rounded-xl glass-flat">
      {m.avatarUrl ? (
        <img src={m.avatarUrl.startsWith('http') ? m.avatarUrl : `${API_ORIGIN}${m.avatarUrl}`}
             alt="" className="w-10 h-10 rounded-full object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-full grid place-items-center text-xs font-bold text-white bg-gradient-to-br from-accent to-accent2">
          {initials || '?'}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate flex items-center gap-1.5">
          {m.displayName}{isMe && <span className="text-[10px] text-app-text-mute">{t('org.yourselfBadge')}</span>}
        </div>
        <div className="text-xs text-app-text-mute truncate">{m.email}</div>
      </div>
      <RolePill role={m.orgRole as any} small />
      {canEdit && (
        <div className="flex items-center gap-1">
          {isOwner && (
            <button onClick={onToggleAdmin} className="text-xs px-2.5 py-1.5 rounded-lg glass-flat hover:bg-app-elev-2 transition">
              {m.orgRole === 'Admin' ? t('org.demoteAdmin') : t('org.promoteAdmin')}
            </button>
          )}
          {isOwner && (
            <button onClick={onPerms} className="text-xs px-2.5 py-1.5 rounded-lg glass-flat hover:bg-app-elev-2 transition" title={t('org.permsTitle')}>
              ⚙️
            </button>
          )}
          <button onClick={onRemove} className="text-xs px-2.5 py-1.5 rounded-lg text-coral hover:bg-coral/10 transition" title={t('org.removeMember')}>
            🗑
          </button>
        </div>
      )}
    </li>
  )
}

// ── Permissions-Modal ─────────────────────────────────────────────────────
function PermissionsModal({
  member, onClose, onSaved,
}: { member: OrgMember; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const [perms, setPerms] = useState<OrgMember['permissions']>(member.permissions)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const toggle = (k: keyof OrgMember['permissions']) =>
    setPerms(p => ({ ...p, [k]: !p[k] }))

  const save = async () => {
    setBusy(true); setErr(null)
    try {
      await setMemberPermissions(member.id, perms)
      setOk(true); setTimeout(() => onSaved(), 600)
    } catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-start sm:place-items-center p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-lg glass-strong p-5 md:p-6 mt-8 sm:mt-0" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-lg">{t('org.permsTitle')}</h3>
          <button onClick={onClose} className="text-app-text-mute hover:text-app-text text-2xl">×</button>
        </header>
        <p className="text-xs text-app-text-mute mb-4">{t('org.permsSub', { name: member.displayName })}</p>

        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {(Object.keys(perms) as Array<keyof OrgMember['permissions']>).map(k => (
            <label key={k} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-app-elev cursor-pointer">
              <input type="checkbox" checked={perms[k]} onChange={() => toggle(k)}
                className="w-4 h-4 rounded accent-accent shrink-0" />
              <span className="text-sm flex-1">{t(`org.permLabels.${k}`)}</span>
            </label>
          ))}
        </div>

        {err && <div className="mt-3 text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
        {ok  && <div className="mt-3 text-sm text-teal">{t('org.permsSaved')}</div>}

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost text-sm py-2 px-4">{t('common.cancel')}</button>
          <button onClick={save} disabled={busy} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
            {busy ? '…' : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Tab: Einladungen ───────────────────────────────────────────────────────
function InvitesTab() {
  const t = useT()
  const lang = useLangCode()
  const [codes, setCodes] = useState<InviteCode[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try { setCodes(await listInviteCodes()) }
    catch { /* ignore */ }
    finally { setLoading(false) }
  }
  useEffect(() => { void refresh() }, [])

  return (
    <div className="space-y-5">
      <section className="glass p-5 md:p-7">
        <header className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold">{t('org.inviteCodesTitle')}</h3>
            <p className="text-xs text-app-text-mute mt-0.5">{t('org.inviteCodesSub')}</p>
          </div>
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm py-2 px-4">
            + {t('org.createCode')}
          </button>
        </header>
        {loading ? <Spinner small /> : codes.length === 0 ? (
          <div className="text-sm text-app-text-mute text-center py-6">{t('org.codeEmpty')}</div>
        ) : (
          <ul className="space-y-2">
            {codes.map(c => (
              <CodeRow key={c.id} c={c} t={t} lang={lang}
                onDelete={async () => {
                  if (!confirm(t('org.codeDeleteConfirm'))) return
                  try { await deleteInviteCode(c.id); refresh() } catch (e: any) { alert(e?.message) }
                }} />
            ))}
          </ul>
        )}
      </section>

      {showForm && (
        <CreateCodeModal onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); refresh() }} />
      )}

      <EmailInviteCard />
    </div>
  )
}

function CodeRow({ c, t, lang, onDelete }: { c: InviteCode; t: any; lang: string; onDelete: () => void }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try { await navigator.clipboard.writeText(c.code); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch {}
  }
  const usesText = c.maxUses
    ? t('org.codeUsedOf', { n: c.usedCount, max: c.maxUses })
    : t('org.codeUsed', { n: c.usedCount })
  const expiryText = c.expiresAt
    ? t('org.codeExpiresAt', { date: new Date(c.expiresAt).toLocaleDateString(lang) })
    : t('org.codeNoExpiry')
  return (
    <li className="flex flex-wrap items-center gap-3 p-3 rounded-xl glass-flat">
      <div className="font-mono text-lg font-bold tracking-widest px-3 py-2 rounded-lg bg-accent/10 border border-accent/25 text-accent">
        {c.code}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-app-text-mute">{usesText} · {expiryText}</div>
      </div>
      <button onClick={copy} className="text-xs px-3 py-1.5 rounded-lg glass-flat hover:bg-app-elev-2 transition">
        {copied ? t('org.codeCopied') : t('org.codeCopy')}
      </button>
      <button onClick={onDelete} className="text-xs px-2.5 py-1.5 rounded-lg text-coral hover:bg-coral/10 transition">🗑</button>
    </li>
  )
}

function CreateCodeModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const t = useT()
  const [uses, setUses] = useState<'unlimited' | '1' | '5' | '10' | '50'>('5')
  const [expiry, setExpiry] = useState<'none' | '1' | '7' | '30'>('30')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setErr(null)
    try {
      await createInviteCode({
        maxUses: uses === 'unlimited' ? null : parseInt(uses),
        expiresInDays: expiry === 'none' ? null : parseInt(expiry),
      })
      onCreated()
    } catch (e: any) { setErr(e?.message ?? 'failed'); setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-md glass-strong p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-lg mb-4">{t('org.createCode')}</h3>

        <label className="block mb-4">
          <span className="text-xs font-bold tracking-wider uppercase text-app-text-dim mb-2 block">{t('org.codeFormUses')}</span>
          <div className="grid grid-cols-3 gap-1 p-1 rounded-xl"
               style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            {([
              { v: 'unlimited' as const, l: t('org.codeFormUsesAny') },
              { v: '1' as const, l: t('org.codeFormUses1') },
              { v: '5' as const, l: '5×' },
              { v: '10' as const, l: '10×' },
              { v: '50' as const, l: '50×' },
            ]).map(o => (
              <button key={o.v} onClick={() => setUses(o.v)}
                className={`py-2 rounded-lg transition text-xs font-medium ${
                  uses === o.v ? 'bg-accent text-white' : 'text-app-text-dim hover:text-app-text'
                }`}>
                {o.l}
              </button>
            ))}
          </div>
        </label>

        <label className="block mb-4">
          <span className="text-xs font-bold tracking-wider uppercase text-app-text-dim mb-2 block">{t('org.codeFormExpiry')}</span>
          <div className="grid grid-cols-2 gap-1 p-1 rounded-xl"
               style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            {([
              { v: 'none' as const, l: t('org.codeFormExpAny') },
              { v: '1' as const, l: t('org.codeFormExp1d') },
              { v: '7' as const, l: t('org.codeFormExp7d') },
              { v: '30' as const, l: t('org.codeFormExp30d') },
            ]).map(o => (
              <button key={o.v} onClick={() => setExpiry(o.v)}
                className={`py-2 rounded-lg transition text-xs font-medium ${
                  expiry === o.v ? 'bg-accent text-white' : 'text-app-text-dim hover:text-app-text'
                }`}>
                {o.l}
              </button>
            ))}
          </div>
        </label>

        {err && <div className="mb-3 text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost text-sm py-2 px-4">{t('common.cancel')}</button>
          <button onClick={submit} disabled={busy} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
            {busy ? '…' : t('org.codeFormSubmit')}
          </button>
        </div>
      </div>
    </div>
  )
}

function EmailInviteCard() {
  const t = useT()
  const [email, setEmail] = useState('')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setOk(null); setBusy(true)
    try {
      await sendEmailInvite(email.trim(), msg.trim() || undefined)
      setOk(t('org.inviteSentOk', { email: email.trim() }))
      setEmail(''); setMsg('')
    } catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }

  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4">
        <h3 className="font-semibold">{t('org.inviteByEmailTitle')}</h3>
        <p className="text-xs text-app-text-mute mt-0.5">{t('org.inviteByEmailSub')}</p>
      </header>
      <form onSubmit={submit} className="space-y-3">
        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder={t('org.inviteEmailPh')} className="input-base" />
        <textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder={t('org.inviteMessagePh')} rows={2} className="input-base" />
        {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
        {ok  && <div className="text-sm text-teal">{ok}</div>}
        <div className="flex justify-end">
          <button type="submit" disabled={busy || !email.trim()} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
            {busy ? '…' : t('org.inviteSend')}
          </button>
        </div>
      </form>
    </section>
  )
}

// ── Tab: Settings ──────────────────────────────────────────────────────────
function SettingsTab({ org, onChanged }: { org: OrgDetail; onChanged: () => void }) {
  const t = useT()
  const nav = useNavigate()
  const [name, setName] = useState(org.name)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true); setOk(false)
    try {
      await updateOrg({ name: name.trim() })
      setOk(true); onChanged(); setTimeout(() => setOk(false), 2000)
    } catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }

  const handleDelete = async () => {
    if (!confirm(t('org.deleteConfirm', { name: org.name }))) return
    setBusy(true); setErr(null)
    try { await deleteOrg(); alert(t('org.deletedOk')); nav('/app', { replace: true }) }
    catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-5">
      <section className="glass p-5 md:p-7">
        <header className="mb-4">
          <h3 className="font-semibold">{t('org.settingsTitle')}</h3>
          <p className="text-xs text-app-text-mute mt-0.5">{t('org.settingsSub')}</p>
        </header>
        <form onSubmit={save} className="space-y-3">
          <label className="block">
            <span className="text-xs font-bold tracking-wider uppercase text-app-text-dim mb-1.5 block">{t('org.nameField')}</span>
            <input value={name} onChange={e => setName(e.target.value)} maxLength={120} className="input-base" />
          </label>
          {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
          {ok && <div className="text-sm text-teal">{t('common.saved')}</div>}
          <div className="flex justify-end">
            <button type="submit" disabled={busy || name === org.name} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
              {busy ? '…' : t('org.saveBtn')}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-3xl p-5 md:p-7"
               style={{ background: 'rgba(255,91,92,0.04)', border: '1px solid rgba(255,91,92,0.25)' }}>
        <h3 className="font-semibold text-coral">{t('org.dangerTitle')}</h3>
        <p className="mt-2 text-sm text-app-text-dim">{t('org.dangerText')}</p>
        <button onClick={handleDelete} disabled={busy}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-coral/15 hover:bg-coral/25 border border-coral/30 text-coral font-semibold text-sm transition disabled:opacity-50">
          {busy ? '…' : t('org.deleteBtn')}
        </button>
      </section>
    </div>
  )
}

// ── Beitritts-View (kein Org) ──────────────────────────────────────────────
function JoinView({ onJoined }: { onJoined: () => void }) {
  const t = useT()
  const { user } = useAuth()
  const { items: extensions } = useExtensions()
  const eligible = canCreateOrg({
    accountType: user?.accountType,
    appRole:     user?.appRole,
    extensions,
  })

  const [code, setCode] = useState('')
  const [preview, setPreview] = useState<OrgPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Org-Erstellung
  const [orgName, setOrgName] = useState('')
  const [createBusy, setCreateBusy] = useState(false)
  const [createErr, setCreateErr] = useState<string | null>(null)
  const [createSuccess, setCreateSuccess] = useState<string | null>(null)

  const checkCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null); setPreview(null)
    try { setPreview(await previewInviteCode(code.trim())) }
    catch (e: any) { setErr(e?.message ?? 'preview failed') }
    finally { setBusy(false) }
  }

  const confirmJoin = async () => {
    if (!preview) return
    setBusy(true); setErr(null)
    try {
      const detail = await joinOrgWithCode(code.trim())
      setSuccess(t('org.joinSuccess', { name: detail.name }))
      setTimeout(() => onJoined(), 1200)
    } catch (e: any) { setErr(e?.message ?? 'join failed') }
    finally { setBusy(false) }
  }

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgName.trim()) return
    setCreateBusy(true); setCreateErr(null)
    try {
      const detail = await createOrg(orgName.trim())
      setCreateSuccess(t('org.createSuccess', { name: detail.name }))
      setTimeout(() => onJoined(), 1200)
    } catch (e: any) { setCreateErr(e?.message ?? 'create failed') }
    finally { setCreateBusy(false) }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12 space-y-5">
      <header className="text-center mb-6">
        <div className="text-6xl mb-4">🏢</div>
        <h1 className="text-2xl md:text-3xl font-bold">{t('org.joinTitle')}</h1>
        <p className="text-app-text-dim mt-2 max-w-md mx-auto">{t('org.joinSub')}</p>
      </header>

      <section className="glass-strong p-6 md:p-8">
        {!preview && !success && (
          <form onSubmit={checkCode} className="space-y-3">
            <input type="text" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder={t('org.joinCodePlaceholder')} maxLength={20} autoComplete="off"
              className="input-base text-center text-xl font-mono tracking-widest uppercase" />
            {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
            <button type="submit" disabled={busy || code.trim().length < 4} className="btn-primary w-full disabled:opacity-50">
              {busy ? '…' : t('org.joinPreview')}
            </button>
          </form>
        )}
        {preview && !success && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-xs font-bold tracking-wider uppercase text-app-text-mute mb-1">{t('org.joinPreviewLabel')}</div>
              <div className="text-2xl font-bold">{preview.orgName}</div>
              <div className="text-sm text-app-text-dim mt-1">{t('org.joinOwnerLabel')}: {preview.ownerName}</div>
              <div className="text-xs text-app-text-mute mt-1">{t('org.joinMemberCount', { n: preview.memberCount })}</div>
            </div>
            {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
            <div className="flex gap-2">
              <button onClick={confirmJoin} disabled={busy} className="btn-primary flex-1 disabled:opacity-50">
                {busy ? '…' : t('org.joinConfirm', { name: preview.orgName })}
              </button>
              <button onClick={() => { setPreview(null); setCode('') }} disabled={busy} className="btn-ghost">
                {t('org.joinCancel')}
              </button>
            </div>
          </div>
        )}
        {success && <div className="text-center py-6"><div className="text-5xl mb-3">🎉</div><div className="text-lg font-bold text-teal">{success}</div></div>}
      </section>

      <section className="rounded-3xl p-5 md:p-7"
               style={{ background: 'rgba(138,68,238,0.06)', border: '1px solid rgba(138,68,238,0.20)' }}>
        <h3 className="font-semibold flex items-center gap-2"><span className="text-lg">🚀</span>{t('org.createTitle')}</h3>
        {eligible ? (
          <>
            <p className="mt-2 text-sm text-app-text-dim leading-relaxed">{t('org.createReady')}</p>
            {createSuccess ? (
              <div className="mt-4 text-center py-4">
                <div className="text-4xl mb-2">🎉</div>
                <div className="text-base font-bold text-teal">{createSuccess}</div>
              </div>
            ) : (
              <form onSubmit={submitCreate} className="mt-4 space-y-3">
                <input
                  type="text"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  placeholder={t('org.createNamePlaceholder')}
                  maxLength={80}
                  className="input-base"
                  autoComplete="organization"
                  disabled={createBusy}
                />
                {createErr && (
                  <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">
                    {createErr}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={createBusy || orgName.trim().length < 2}
                  className="btn-primary w-full disabled:opacity-50">
                  {createBusy ? '…' : t('org.createBtn')}
                </button>
              </form>
            )}
          </>
        ) : (
          <>
            <p className="mt-2 text-sm text-app-text-dim leading-relaxed">{t('org.createNeedsBiz')}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/app/shop" className="btn-primary text-sm py-2.5 px-4">🛒 {t('org.createGoShop')}</Link>
              <a href="https://apps.apple.com/app/id6766051168" target="_blank" rel="noreferrer"
                 className="btn-ghost text-sm py-2.5 px-4"> {t('org.createOpenApp')}</a>
            </div>
          </>
        )}
      </section>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function RolePill({ role, small }: { role: 'Owner' | 'Admin' | 'Member' | string; small?: boolean }) {
  const t = useT()
  const map: any = {
    Owner:  { bg: 'rgba(255,179,0,0.15)',   border: 'rgba(255,179,0,0.30)',   color: '#ffb300', text: t('org.roleOwner') },
    Admin:  { bg: 'rgba(138,68,238,0.15)', border: 'rgba(138,68,238,0.30)', color: '#8a44ee', text: t('org.roleAdmin') },
    Member: { bg: 'rgba(255,255,255,0.06)', border: 'var(--border-strong)',   color: 'var(--text-dim)', text: t('org.roleMember') },
  }
  const m = map[role] ?? map.Member
  const cls = small ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'
  return (
    <span className={`inline-flex items-center font-bold tracking-wider rounded-full uppercase border ${cls}`}
          style={{ background: m.bg, color: m.color, borderColor: m.border }}>
      {m.text}
    </span>
  )
}

function Pill({ icon, text }: { icon: string; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium glass-flat">
      <span>{icon}</span> {text}
    </span>
  )
}

function OrgLogo({ logoUrl, name }: { logoUrl: string | null; name: string }) {
  if (logoUrl) {
    const src = logoUrl.startsWith('http') ? logoUrl : `${API_ORIGIN}${logoUrl}`
    return <img src={src} alt="" className="w-20 h-20 rounded-3xl object-cover" style={{ border: '1px solid var(--border-strong)' }} />
  }
  const init = name.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()
  return (
    <div className="w-20 h-20 rounded-3xl grid place-items-center text-2xl font-bold text-white bg-gradient-to-br from-brandBlue to-accent"
         style={{ border: '1px solid var(--border-strong)' }}>
      {init || '?'}
    </div>
  )
}

function Spinner({ small }: { small?: boolean }) {
  const sz = small ? 'w-6 h-6 border-2' : 'w-10 h-10 border-4'
  return (
    <div className="flex items-center justify-center py-12">
      <div className={`${sz} rounded-full border-app-border border-t-accent animate-spin`} />
    </div>
  )
}

function productEmoji(slug: string): string {
  const m: Record<string, string> = {
    PunchClock: '🕐', TerminalMode: '🖥️', Recruitment: '📋',
    BusinessBasic: '🏢', BusinessL: '🏢',
    BusinessBasicYearly: '🏢', BusinessLYearly: '🏢',
    MoreSpace: '💾',
  }
  return m[slug] ?? '📦'
}
